import React, { useEffect, useState } from 'react';

import { useAuth } from '../../../common/hooks/useAuth';
import { useSzafir } from '../../../common/hooks/useSzafir';
import { useSpdCore } from '../../../common/hooks/useSpdCore';
import { responseErrors } from '../../../common/spdCore/validation/responseErrors';
import {
  GetCardDto,
  ResponseError,
} from '../../../common/spdCore/autogenerated/spdApiClient';
import ConfirmationButton from '../../../app/components/ConfirmationButton';

import { SigningInProgressModal } from './SigningInProgressModal';

interface MultipleCardsSigningProps {
  onSignedFinished: (
    errors: string[],
    allCount: number,
    errorCount: number,
  ) => Promise<void> | void | undefined;
  userUid: string;
}

export const MultipleCardsSigning: React.FC<
  MultipleCardsSigningProps
> = props => {
  const [signingInProgress, setSigningInProgress] = useState(false);
  const [sendSummary, setSendSummary] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [allCount, setAllCount] = useState<number>(0);
  const [errorCount, setErrorCount] = useState<number>(0);
  const [progress, setProgress] = useState<string>('');
  const auth = useAuth();
  const spd = useSpdCore();
  const szafir = useSzafir();

  useEffect(() => {
    if (sendSummary) {
      void props.onSignedFinished(errors, allCount, errorCount);
      setSendSummary(false);
      setProgress('');
      setErrors([]);
      setAllCount(0);
      setErrorCount(0);
    }
  }, [errors, errorCount, allCount, props, sendSummary]);

  const onSign = async () => {
    setSigningInProgress(true);
    let cards: GetCardDto[] = [];
    try {
      cards = await spd.cards.getAllForUserToSign(props.userUid);
    } catch (response) {
      const errors = await responseErrors(response as ResponseError);
      setErrors(prevArray => [
        ...prevArray,
        `Błąd pobierania dokumentów do podpisu dla userUid: ${props.userUid}`,
        ...errors,
      ]);
      setSigningInProgress(false);
      setSendSummary(true);
      return;
    }

    setAllCount(cards.length);
    for (let index = 0; index < cards.length; index++) {
      const card = cards[index];
      setProgress(`podpisywanie ${index + 1} z ${cards.length}`);
      await signAndSave(card);
      console.log(
        `Podpisano kartę o id: ${card.id} - ${index + 1} z ${cards.length}`,
      );
    }
    setSigningInProgress(false);
    setSendSummary(true);
  };

  const signAndSave = async (card: GetCardDto) => {
    try {
      const taskList = await spd.szafir.getTaskListForCard(card.id);

      const [result, documentId] = await new Promise<[Buffer | null, string]>(
        (resolve, reject) => {
          void szafir.signDocument(
            taskList,
            auth.userProfile?.pin || '',
            card.id,
            '',
            false,
            (result, documentId) => resolve([result, documentId]),
            (error, documentId) => {
              signedFailed(error, documentId);
              reject(new Error(error));
              console.log('Błąd podpisywania karty o id: ', documentId);
            },
          );
        },
      );
      await saveSignedPdf(result, documentId);
    } catch (response) {
      setErrorCount(prevCount => prevCount + 1);
      const errors = await responseErrors(response as ResponseError);
      setErrors(prevArray => [
        ...prevArray,
        `Błąd podpisywania karty o numerze: ${card?.number}`,
        ...errors,
      ]);
    }
  };

  const saveSignedPdf = async (result: Buffer | null, documentId: string) => {
    await spd.savedPdfs
      .savePdfFileForCard({
        entityId: documentId,
        file: result,
      })
      .catch(async response => {
        setErrorCount(prevCount => prevCount + 1);
        const errors = await responseErrors(response as ResponseError);
        setErrors(prevArray => [
          ...prevArray,
          `Błąd podpisywania karty o id: ${documentId} - `,
          ...errors,
        ]);
      });
  };

  const signedFailed = (error: string, documentId: string) => {
    setErrorCount(prevCount => prevCount + 1);
    setErrors(prevArray => [
      ...prevArray,
      `Błąd podpisywania karty o id: ${documentId} - `,
      error,
    ]);
  };

  if (signingInProgress) {
    return <SigningInProgressModal message={progress} />;
  }

  return (
    <ConfirmationButton
      className="mb-3"
      confirmation="Czy na pewno rozpocząć podpisywanie wszystkich dostępnych do podpisu kart?"
      variant="outline-primary"
      onOK={() => onSign()}
    >
      Podpisz wszystkie swoje karty
    </ConfirmationButton>
  );
};
